import { useCallback, useEffect, useState } from "react";
import {
  acceptQuote,
  createQuote,
  deleteQuote,
  emailInvoice,
  emailQuote,
  generateInvoice,
  getInvoice,
  getQuotes,
  rejectQuote,
  sendInvoice,
  sendQuote,
  updateQuote,
} from "../../../lib/api";
import { formatPriceCents } from "../lib/jobForm";
import type { InvoiceRecord, QuoteRecord } from "../types";
import { InvoicePaymentsPanel } from "../../documents/InvoicePaymentsPanel";
import { Modal } from "../../../components/Modal";
import form from "./NewJobForm.module.css";
import styles from "./DocumentsSection.module.css";

/** Default quote validity window (DEC: expiry editable at creation). */
const DEFAULT_EXPIRY_DAYS = 30;

function defaultExpiry(): string {
  return new Date(Date.now() + DEFAULT_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

export interface DocumentsSectionProps {
  jobId: string;
  /** Drives invoice availability: generating requires a Done job. */
  jobStatus: string;
  /** Notifies the host that a quote was accepted so board state stays fresh. */
  onQuoteAccepted?: () => void;
}

/**
 * Quotes & invoicing for one job, rendered inside the Scope page
 * (US-QI-005). Lists the job's quotes and invoice with the lifecycle actions
 * the server allows — this component enforces nothing itself; illegal actions
 * surface the server's error message. Owns its fetch state only; the job draft
 * is untouched.
 */
export function DocumentsSection({ jobId, jobStatus, onQuoteAccepted }: DocumentsSectionProps) {
  const [quotes, setQuotes] = useState<QuoteRecord[] | null>(null);
  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [expiryDate, setExpiryDate] = useState(defaultExpiry);
  const [paymentsInvoice, setPaymentsInvoice] = useState<InvoiceRecord | null>(null);
  const [previewQuoteId, setPreviewQuoteId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [quotesBody, invoiceBody] = await Promise.all([getQuotes(jobId), getInvoice(jobId)]);
      setQuotes(quotesBody.quotes);
      setInvoice(invoiceBody.invoice);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load documents");
    }
  }, [jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(
    async (action: () => Promise<void>): Promise<void> => {
      if (busy) return;
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await action();
        await load();
      } catch (actionError) {
        setError(actionError instanceof Error ? actionError.message : "Action failed");
      } finally {
        setBusy(false);
      }
    },
    [busy, load],
  );

  const handleCreateQuote = (): void =>
    void run(async () => {
      const result = await createQuote(jobId, { expiryDate: expiryDate || undefined });
      setPreviewQuoteId(result.quote.id);
    });

  const handlePreview = (quoteId: string): void => {
    setPreviewQuoteId((currentId) => (currentId === quoteId ? null : quoteId));
  };

  const handleSend = (quote: QuoteRecord): void =>
    void run(() => sendQuote(quote.id).then(() => undefined));

  const handleAccept = (quote: QuoteRecord): void =>
    void run(async () => {
      await acceptQuote(quote.id);
      onQuoteAccepted?.();
    });

  const handleReject = (quote: QuoteRecord): void =>
    void run(() => rejectQuote(quote.id).then(() => undefined));

  const handleDelete = (quote: QuoteRecord): void =>
    void run(() => deleteQuote(quote.id).then(() => undefined));

  const handleExtend = (quote: QuoteRecord): void =>
    void run(() => updateQuote(quote.id, { expiryDate: defaultExpiry() }).then(() => undefined));

  const handleGenerateInvoice = (): void =>
    void run(() => generateInvoice(jobId).then(() => undefined));

  const handleSendInvoice = (): void =>
    void run(() => sendInvoice(invoice?.id ?? "").then(() => undefined));

  const handleEmailQuote = (quote: QuoteRecord): void =>
    void run(async () => {
      const result = await emailQuote(quote.id);
      setNotice(
        result.delivered
          ? `Quote emailed to ${result.recipients.length} address${result.recipients.length === 1 ? "" : "es"}.`
          : `SMTP not configured — quote email logged for ${result.recipients.length} address${result.recipients.length === 1 ? "" : "es"}.`,
      );
    });

  const handleEmailInvoice = (): void =>
    void run(async () => {
      const result = await emailInvoice(invoice?.id ?? "");
      setNotice(
        result.delivered
          ? `Invoice emailed to ${result.recipients.length} address${result.recipients.length === 1 ? "" : "es"}.`
          : `SMTP not configured — invoice email logged for ${result.recipients.length} address${result.recipients.length === 1 ? "" : "es"}.`,
      );
    });

  return (
    <section className={form.section}>
      <h3 className={form.sectionTitle}>Quotes &amp; invoicing</h3>

      {quotes === null ? (
        <span className={form.note}>Loading documents…</span>
      ) : (
        <ul className={styles.list}>
          {quotes.map((quote) => (
            <li key={quote.id} className={styles.docItem}>
              <div className={styles.docRow}>
                <span className={styles.docTitle}>
                  {quote.number} · v{quote.version}
                </span>
                <span className={`${styles.badge} ${badgeClass(quote)}`}>
                  {quote.expired ? "expired" : quote.status}
                </span>
                {quote.expiryDate ? (
                  <span className={styles.docMeta}>expires {quote.expiryDate}</span>
                ) : null}
                <span className={styles.docTotal}>{formatPriceCents(quote.totalCents)}</span>
                <span className={styles.actions}>
                  <button
                    type="button"
                    className={styles.action}
                    aria-expanded={previewQuoteId === quote.id}
                    aria-controls={`quote-preview-${quote.id}`}
                    onClick={() => handlePreview(quote.id)}
                  >
                    {previewQuoteId === quote.id ? "Hide preview" : "Preview"}
                  </button>
                  {quote.status === "draft" && (
                    <button type="button" className={styles.action} disabled={busy} onClick={() => handleSend(quote)}>
                      Send
                    </button>
                  )}
                  {quote.status === "sent" && (
                    <>
                      <button type="button" className={styles.action} disabled={busy} onClick={() => handleAccept(quote)}>
                        Accept
                      </button>
                      <button type="button" className={styles.action} disabled={busy} onClick={() => handleReject(quote)}>
                        Reject
                      </button>
                      {quote.expired && (
                        <button type="button" className={styles.action} disabled={busy} onClick={() => handleExtend(quote)}>
                          Extend 30 days
                        </button>
                      )}
                    </>
                  )}
                  {(quote.status === "draft" || quote.status === "sent") && (
                    <button type="button" className={styles.actionDanger} disabled={busy} onClick={() => handleDelete(quote)}>
                      Delete
                    </button>
                  )}
                  {(quote.status === "sent" || quote.status === "accepted") && (
                    <>
                      <a className={styles.action} href={`/api/quotes/${quote.id}/pdf`} target="_blank" rel="noreferrer">
                        PDF
                      </a>
                      <button type="button" className={styles.action} disabled={busy} onClick={() => handleEmailQuote(quote)}>
                        Email
                      </button>
                    </>
                  )}
                </span>
              </div>
              {previewQuoteId === quote.id && (
                <div id={`quote-preview-${quote.id}`} className={styles.preview}>
                  <iframe
                    className={styles.previewFrame}
                    src={`/api/quotes/${quote.id}/pdf`}
                    title={`Quote preview — ${quote.number}`}
                  />
                </div>
              )}
            </li>
          ))}
          {quotes.length === 0 && <li className={form.note}>No quotes yet.</li>}
        </ul>
      )}

      <div className={styles.createRow}>
        <label className={styles.createField}>
          <span className={form.label}>Quote expiry</span>
          <input
            className={form.input}
            type="date"
            name="quoteExpiry"
            value={expiryDate}
            onChange={(event) => setExpiryDate(event.currentTarget.value)}
          />
        </label>
        <button type="button" className={styles.createButton} disabled={busy} onClick={handleCreateQuote}>
          Create quote
        </button>
      </div>

      <div className={styles.invoiceBlock}>
        <span className={form.label}>Invoice</span>
        {invoice ? (
          <div className={styles.docRow}>
            <span className={styles.docTitle}>{invoice.number}</span>
            <span className={`${styles.badge} ${invoice.status === "sent" ? styles.badgeAccepted : styles.badgeSent}`}>
              {invoice.status}
            </span>
            <span className={styles.docMeta}>
              {invoice.source === "quote" ? "from accepted quote" : "from scope"}
            </span>
            <span className={styles.docTotal}>{formatPriceCents(invoice.totalCents)}</span>
            {invoice.status === "draft" && (
              <span className={styles.actions}>
                <button type="button" className={styles.action} disabled={busy} onClick={handleGenerateInvoice}>
                  Refresh
                </button>
                <button type="button" className={styles.action} disabled={busy} onClick={handleSendInvoice}>
                  Send
                </button>
              </span>
            )}
            {invoice.status === "sent" && (
              <span className={styles.actions}>
                <button type="button" className={styles.action} disabled={busy} onClick={() => setPaymentsInvoice(invoice)}>
                  Payments
                </button>
                <a className={styles.action} href={`/api/invoices/${invoice.id}/pdf`} target="_blank" rel="noreferrer">
                  PDF
                </a>
                <button type="button" className={styles.action} disabled={busy} onClick={handleEmailInvoice}>
                  Email
                </button>
              </span>
            )}
            {invoice.status === "paid" && (
              <span className={styles.actions}>
                <button type="button" className={styles.action} disabled={busy} onClick={() => setPaymentsInvoice(invoice)}>
                  Payments
                </button>
                <a className={styles.action} href={`/api/invoices/${invoice.id}/pdf`} target="_blank" rel="noreferrer">
                  PDF
                </a>
              </span>
            )}
          </div>
        ) : jobStatus === "done" ? (
          <button type="button" className={styles.createButton} disabled={busy} onClick={handleGenerateInvoice}>
            Generate invoice
          </button>
        ) : (
          <span className={form.note}>Generating an invoice unlocks when the job reaches Done.</span>
        )}
      </div>

      {notice && <p className={form.note}>{notice}</p>}
      {error && (
        <p className={form.error} role="alert">
          {error}
        </p>
      )}

      {paymentsInvoice && (
        <Modal
          title={`Payments — ${paymentsInvoice.number}`}
          onClose={() => setPaymentsInvoice(null)}
        >
          <InvoicePaymentsPanel invoice={paymentsInvoice} onChanged={() => void load()} />
        </Modal>
      )}
    </section>
  );
}

function badgeClass(quote: QuoteRecord): string {
  if (quote.expired) return styles.badgeExpired;
  switch (quote.status) {
    case "accepted":
      return styles.badgeAccepted;
    case "sent":
      return styles.badgeSent;
    case "rejected":
    case "withdrawn":
      return styles.badgeMuted;
    default:
      return styles.badgeDraft;
  }
}
